import { useEffect, useState, type ReactElement } from 'react';
import type { Effect } from '@schnitt/shared';
import { cssFilterVon, vignetteVon } from '../playback';
import { FarbraederFilter, GammaFilter, LookFilter, gammaOf, lutOf, raederOf } from '../looks';

/**
 * Eine Einstellung als Kachel mit dem eigenen Bild darin (7.16).
 *
 * „Jede Einstellung als Kachel mit dem *eigenen* Bild darin, statt als Name in
 * einer Liste — man sieht, was passiert, bevor man es wählt“ (Sascha, 4.
 * September). „Kino“, „Warm“, „Kräftig“ sind Wörter, unter denen sich jeder
 * etwas anderes vorstellt; ein Bild ist ein Bild.
 *
 * Gerechnet wird nicht neu: die Kachel geht durch dieselbe `cssFilterVon` und
 * denselben SVG-Filter wie die Leinwand. Eine zweite Umrechnung daneben wäre
 * eine Kachel, die etwas anderes zeigt als das Bild danach -- und das ist
 * schlimmer als gar keine Kachel.
 */
export function EffektKachel({
  bild,
  effects,
  label,
  aktiv,
  kennung,
  onClick,
}: {
  /** Der Frame, auf dem gezeigt wird -- oder null, dann bleibt die Fläche leer. */
  bild: string | null;
  effects: Effect[];
  label: string;
  aktiv: boolean;
  /** Eindeutig je Kachel: daran hängt die Kennung des SVG-Filters. */
  kennung: string;
  onClick: () => void;
}): ReactElement {
  const lut = lutOf(effects);
  const raeder = raederOf(effects);
  const gamma = gammaOf(effects);
  const vignette = vignetteVon(effects);
  return (
    <button
      className={`effekt-kachel ${aktiv ? 'active' : ''}`}
      aria-pressed={aktiv}
      data-kachel={kennung}
      onClick={onClick}
    >
      <span className="kachel-bild">
        {bild ? (
          <img src={bild} alt="" style={{ filter: cssFilterVon(effects, `kachel-${kennung}`) }} data-kachel-bild />
        ) : (
          // Kein Bild da (ein Tonclip, oder das Gerät kann die Vorschau nicht
          // dekodieren): dann sagt die Kachel das, statt eine schwarze Fläche
          // zu zeigen, die aussieht wie ein Fehler.
          <span className="kachel-ohne" data-kachel-ohne>
            kein Bild
          </span>
        )}
        {vignette > 0 ? <span className="kachel-vignette" style={{ opacity: vignette }} /> : null}
        {lut ? (
          <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
            <LookFilter clipId={`kachel-${kennung}`} name={lut.name} intensity={lut.intensity} />
          </svg>
        ) : null}
        {raeder ? (
          <FarbraederFilter clipId={`kachel-${kennung}`} tiefen={raeder.tiefen} mitten={raeder.mitten} hoehen={raeder.hoehen} />
        ) : null}
        {gamma !== 1 ? <GammaFilter clipId={`kachel-${kennung}`} gamma={gamma} /> : null}
      </span>
      <span className="kachel-name">{label}</span>
    </button>
  );
}

/**
 * Der Frame, auf dem die Kacheln zeigen.
 *
 * Am liebsten der unter dem Abspielkopf -- das ist die Stelle, um die es
 * gerade geht. Er wird einmal abgegriffen, wenn das Blatt aufgeht, und nicht
 * bei jedem Bild neu: eine Kachelreihe, die beim Scrubben flackert, hilft
 * niemandem.
 *
 * Geht das nicht -- ein Tonclip, ein Gerät, das die Vorschau nicht dekodieren
 * kann --, nimmt sie das Standbild des Clips. Und geht auch das nicht, bleibt
 * sie ehrlich leer.
 */
export function useKachelbild(posterUrl: string | undefined): string | null {
  const [bild, setBild] = useState<string | null>(null);
  useEffect(() => {
    const video = [...document.querySelectorAll<HTMLVideoElement>('.canvas video')].find(
      (v) => v.readyState >= 2 && v.videoWidth > 0,
    );
    if (video) {
      try {
        const leinwand = document.createElement('canvas');
        const breite = 192;
        leinwand.width = breite;
        leinwand.height = Math.max(1, Math.round((breite * video.videoHeight) / video.videoWidth));
        const stift = leinwand.getContext('2d');
        if (stift) {
          stift.drawImage(video, 0, 0, leinwand.width, leinwand.height);
          setBild(leinwand.toDataURL('image/jpeg', 0.72));
          return;
        }
      } catch {
        // Ein Bild aus fremder Quelle macht die Leinwand unlesbar. Dann eben
        // das Standbild -- das liegt bei uns.
      }
    }
    setBild(posterUrl ?? null);
  }, [posterUrl]);
  return bild;
}
